import React from "react";
import { CardComp } from "../../../components";
import { formatMoney } from "../../../utils";
import { WEEK_OF_MONTH_OPTIONS, DAY_OF_WEEK_OPTIONS } from "../../../constants/loanModel";

/**
 * Informal (flexible) loan calculator result.
 * Does not show total payable or amortization table. Shows principal, rate, meeting date,
 * explanation (interest = rate% × current balance each month), and optional projection from backend.
 */
const formatMeetingDateLabel = (week, day) => {
  if (!week && !day) return null;
  const w = WEEK_OF_MONTH_OPTIONS.find((o) => o.value === week);
  const d = DAY_OF_WEEK_OPTIONS.find((o) => o.value === day);
  if (w && d) return `${w.label}, ${d.label}`;
  if (w) return w.label;
  if (d) return d.label;
  return null;
};

const InformalLoanCalculationResult = ({ data }) => {
  const isObject = data != null && typeof data === "object" && !Array.isArray(data);
  const principal = isObject
    ? Number(data.principal ?? data.loan_amount ?? data.amount ?? 0)
    : 0;
  const rate = isObject
    ? Number(data.rate ?? data.loan_type?.rate ?? 0)
    : 0;
  const meetingDateLabel = isObject
    ? formatMeetingDateLabel(
        data.interest_application_week ?? data.loan_type?.interest_application_week,
        data.interest_application_day ?? data.loan_type?.interest_application_day
      )
    : null;
  const nextInterestDate = isObject && (data.next_interest_application_date ?? data.meeting_date);
  const projection = isObject && (data.projection ?? data.estimated_interest_12_months ?? data.estimated_interest);

  return (
    <CardComp cardClassName="border-0 shadow-sm pt-4 pb-5">
      <div className="px-3">
        <h5 className="fw-bold mb-3 text-black1">Informal loan (flexible repayment)</h5>
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-6">
            <p className="text-muted mb-1 small fw-semibold">Principal</p>
            <p className="fw-bold text-dark mb-0">
              {formatMoney(principal.toFixed(2))}
            </p>
          </div>
          <div className="col-12 col-md-6">
            <p className="text-muted mb-1 small fw-semibold">Interest rate</p>
            <p className="fw-bold text-dark mb-0">{rate.toFixed(1)}%</p>
          </div>
          {meetingDateLabel && (
            <div className="col-12 col-md-6">
              <p className="text-muted mb-1 small fw-semibold">Meeting date (interest application)</p>
              <p className="fw-bold text-dark mb-0">{meetingDateLabel}</p>
            </div>
          )}
          {nextInterestDate && (
            <div className="col-12 col-md-6">
              <p className="text-muted mb-1 small fw-semibold">Next interest application date</p>
              <p className="fw-bold text-dark mb-0">
                {typeof nextInterestDate === "string"
                  ? nextInterestDate
                  : nextInterestDate}
              </p>
            </div>
          )}
        </div>
        <div className="rounded p-3 mb-4" style={{ background: "var(--gray3, #f7f7f7)" }}>
          <p className="text-black1 mb-0 small">
            Interest is applied <strong>monthly on the meeting date</strong> as{" "}
            <strong>{rate.toFixed(1)}%</strong> of your <strong>current balance</strong>.
            There is no fixed total payable upfront—the longer the loan stays, the more interest accrues.
            You can pay any amount at any time; payments reduce your balance.
          </p>
        </div>
        {projection != null && (
          <div className="rounded p-3" style={{ border: "1px solid var(--gray2, #e5e5e5)" }}>
            <p className="text-muted mb-1 small fw-semibold">Projection (if no payments)</p>
            <p className="fw-bold text-dark mb-0">
              {typeof projection === "number"
                ? `Estimated interest ≈ ${formatMoney(Number(projection).toFixed(2))} over 12 months`
                : String(projection)}
            </p>
          </div>
        )}
      </div>
    </CardComp>
  );
};

export default InformalLoanCalculationResult;
